import { useDispatch } from "react-redux";
import { useCallback } from "react";
import { QuestionInputReducerAction, AbortStageLevel, RequestTriggerType } from "../../../Store/QuestionInputReducer";
import { ChatComponentReducerAction } from "../../../Store/ChatComponentReducer";
import {
    setDefaultTemplateIdInStore,
    setSaveTemplateIdInStore,
    setSystemDefaultContentInStore,
    setUserDefaultContentInStore
} from "../../../Store/TemplateContents";

/**
 * Custom hook for encapsulating all Redux dispatch actions
 * This reduces tight coupling and makes dispatch logic reusable
 */
export const useReduxActions = () => {
    const dispatch = useDispatch();

    // Question Input Actions
    const updateAnswerGenerationActive = useCallback(
        (active: boolean) => {
            dispatch(QuestionInputReducerAction.updateAnswerGenerationActive(active));
        },
        [dispatch]
    );

    const resetAnswerGenerationActiveDetails = useCallback(() => {
        dispatch(QuestionInputReducerAction.resetAnswerGenerationActiveDetails());
    }, [dispatch]);

    const updateAbortStageLevel = useCallback(
        (level: AbortStageLevel) => {
            dispatch(QuestionInputReducerAction.updateAbortStageLevel(level));
        },
        [dispatch]
    );

    const updateServerSideFileAttachmentsIds = useCallback(
        (ids: any[]) => {
            dispatch(QuestionInputReducerAction.updateServerSideFileAttachmentsIds(ids));
        },
        [dispatch]
    );

    const updateRequestTriggerType = useCallback(
        (type: RequestTriggerType) => {
            dispatch(QuestionInputReducerAction.updateRequestTriggerType(type));
        },
        [dispatch]
    );

    const updateTemplateAppliedFlag = useCallback(
        (flag: boolean) => {
            dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(flag));
        },
        [dispatch]
    );

    const resetQuestionInputHistoryStacks = useCallback(() => {
        dispatch(QuestionInputReducerAction.resetQuestionInputHistoryStacks());
    }, [dispatch]);

    const updateActiveFileAttachmentTour = useCallback(
        (flag: boolean) => {
            dispatch(QuestionInputReducerAction.updateActiveFileAttachmentTour(flag));
        },
        [dispatch]
    );

    const updateAnswerGenerationActiveDetails = useCallback(
        (details: any) => {
            dispatch(QuestionInputReducerAction.updateAnswerGenerationActiveDetails(details));
        },
        [dispatch]
    );

    // Chat Component Actions
    const updateCurrentSessionId = useCallback(
        (sessionId: string | null) => {
            dispatch(ChatComponentReducerAction.updateCurrentSessionId(sessionId));
        },
        [dispatch]
    );

    const resetOverAllCurrentSessionDataPoints = useCallback(() => {
        dispatch(ChatComponentReducerAction.resetOverAllCurrentSessionDataPoints());
    }, [dispatch]);

    const updateOverAllCurrentSessionDataPoints = useCallback(
        (dataPoints: any) => {
            dispatch(ChatComponentReducerAction.updateOverAllCurrentSessionDataPoints(dataPoints));
        },
        [dispatch]
    );

    // Template Actions
    const setUserDefaultContent = useCallback(
        (content: any) => {
            dispatch(setUserDefaultContentInStore(content));
        },
        [dispatch]
    );

    const setSystemDefaultContent = useCallback(
        (content: any) => {
            dispatch(setSystemDefaultContentInStore(content));
        },
        [dispatch]
    );

    const setDefaultTemplateId = useCallback(
        (id: string) => {
            dispatch(setDefaultTemplateIdInStore(id));
        },
        [dispatch]
    );

    const setSaveTemplateId = useCallback(
        (id: string) => {
            dispatch(setSaveTemplateIdInStore(id));
        },
        [dispatch]
    );

    // Batch actions for common operations
    const clearChatSession = useCallback(() => {
        dispatch(QuestionInputReducerAction.resetQuestionInputHistoryStacks());
        dispatch(QuestionInputReducerAction.resetAnswerGenerationActiveDetails());
        dispatch(QuestionInputReducerAction.updateServerSideFileAttachmentsIds([]));
        dispatch(ChatComponentReducerAction.updateCurrentSessionId(null));
        dispatch(ChatComponentReducerAction.resetOverAllCurrentSessionDataPoints());
    }, [dispatch]);

    const initializeAnswerGeneration = useCallback(
        (details: any) => {
            dispatch(QuestionInputReducerAction.updateAnswerGenerationActive(true));
            dispatch(QuestionInputReducerAction.updateAnswerGenerationActiveDetails(details));
            dispatch(QuestionInputReducerAction.updateAbortStageLevel("RequestInitiated"));
        },
        [dispatch]
    );

    const finalizeAnswerGeneration = useCallback(() => {
        dispatch(QuestionInputReducerAction.resetAnswerGenerationActiveDetails());
    }, [dispatch]);

    return {
        // Question Input Actions
        updateAnswerGenerationActive,
        resetAnswerGenerationActiveDetails,
        updateAbortStageLevel,
        updateServerSideFileAttachmentsIds,
        updateRequestTriggerType,
        updateTemplateAppliedFlag,
        resetQuestionInputHistoryStacks,
        updateActiveFileAttachmentTour,
        updateAnswerGenerationActiveDetails,

        // Chat Component Actions
        updateCurrentSessionId,
        resetOverAllCurrentSessionDataPoints,
        updateOverAllCurrentSessionDataPoints,

        // Template Actions
        setUserDefaultContent,
        setSystemDefaultContent,
        setDefaultTemplateId,
        setSaveTemplateId,

        // Batch Actions
        clearChatSession,
        initializeAnswerGeneration,
        finalizeAnswerGeneration
    };
};
